// src/domains/shell.ts —— 外壳原生（06 C-01 登录 / C-01b 首登向导 / C-24 侧栏·页头·账号区 / G-10 权限显隐）
import { ApiError } from '../../../shared/src/api/client';
import type { AdminLoginResp, AdminPage } from '../../../shared/src/api/types';
import type { DcHost } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { createPoller, type Poller } from '../../../shared/src/poll';
import { qrDataUrl } from '../../../shared/src/qr';
import { writeLocal } from '../../../shared/src/storage';
import { I18N_ADMIN_ERRORS } from '../i18n/errors';
import { I18N_ADMIN_SHELL, REWRITES_ADMIN_SHELL } from '../i18n/shell';
import { openMyPwd } from '../pages/mypwd';
import { openMyTg } from '../pages/mytg';
import { pageDefOf } from '../pages/index';
import { tabStyle } from '../pages/ui';
import { canSee, hasAnyModule, type AdminMe } from '../perm';
import type { AdminState, BadgeKey, OnbState, Page, ShellSlice } from '../state';
import { startUpChip, stopUpChip, upChipVals } from './dash.upstream';
import { interceptSettingsLeave } from './settings.leave';
import { KEYS } from './shell.keys';
import { str, type AdminCtx, type AdminDomain, type InputEvent } from './types';

export type NavDef = { kind: 'group'; label: string } | { kind: 'item'; page: Page; icon: string; badge?: BadgeKey };

// C-24 顺序；图标取原型 L681，新增页图标取 06 C-24 / C-25～C-30
export const NAV: readonly NavDef[] = [
  { kind: 'group', label: 'gOverview' },
  { kind: 'item', page: 'dashboard', icon: '◫' },
  { kind: 'item', page: 'profit', icon: '¥' }, // 0047 利润台账（06 C-32）
  { kind: 'group', label: 'gUser' },
  { kind: 'item', page: 'users', icon: '◉' },
  { kind: 'item', page: 'cards', icon: '▭' },
  { kind: 'item', page: 'cardActions', icon: '⇄' },
  { kind: 'item', page: 'tx', icon: '≡' },
  { kind: 'item', page: 'wallets', icon: '₮' },
  { kind: 'item', page: 'review', icon: '✓', badge: 'review' },
  { kind: 'item', page: 'treasury', icon: '⛁' },
{kind:'item',page:'cryptoPayments',icon:'₮'},
  { kind: 'group', label: 'gAgent' },
  { kind: 'item', page: 'agents', icon: '♛' },
  { kind: 'item', page: 'agentUsers', icon: '◈' },
  { kind: 'item', page: 'commissions', icon: '％' },
  { kind: 'item', page: 'agentWd', icon: '↑', badge: 'agentWd' },
  { kind: 'group', label: 'gProduct' },
  { kind: 'item', page: 'products', icon: '▤' },
  { kind: 'item', page: 'settings', icon: '⚙' },
  { kind: 'item', page: 'tgbot', icon: '✈' },
  { kind: 'item', page: 'broadcast', icon: '✉' }, // 0049 Bot 广告推送（06 C-33；紧随 Telegram Bot）
  { kind: 'group', label: 'gUpstream' },
  { kind: 'item', page: 'payout', icon: '↗' },
  { kind: 'item', page: 'orders', icon: '⛏' },
  { kind: 'item', page: 'budgets', icon: '◫' },
  { kind: 'item', page: 'upstreamCosts', icon: '¤' },
  { kind: 'item', page: 'upstream', icon: '⇅' },
  { kind: 'item', page: 'logs', icon: '☷', badge: 'logs' },
  { kind: 'item', page: 'alerts', icon: '⚠', badge: 'alerts' },
  { kind: 'group', label: 'gSystem' },
  { kind: 'item', page: 'cms', icon: '✎' },
  { kind: 'item', page: 'admins', icon: '☗' },
  { kind: 'item', page: 'roles', icon: '⚿' },
  { kind: 'item', page: 'audit', icon: '☰' },
];

export const PAGE_ORDER: readonly Page[] = NAV.flatMap((n) => (n.kind === 'item' ? [n.page] : []));

export function firstVisible(visible: (p: Page) => boolean): Page | null {
  return PAGE_ORDER.find(visible) ?? null;
}

export function shellDefaults(): ShellSlice {
  return { me: null, loginPwd: '', loginOtp: '', loginErr: '', otpErr: false, busy: '', onb: null, badges: { review: 0, agentWd: 0, logs: 0, alerts: 0 }, timezone: '', idleLeft: 0 };
}

const BADGES0: ShellSlice['badges'] = { review: 0, agentWd: 0, logs: 0, alerts: 0 };

// 原型 pg.table 表格页集合（L932）+ C-25 / C-26 / C-27 / C-29 / C-30 新增五页；tgbot 只在页签 0 为表格
const TABLE_PAGES: readonly Page[] = [
  'admins', 'users', 'cards', 'tx', 'wallets', 'review', 'agents', 'agentUsers', 'commissions', 'agentWd', 'products', 'payout', 'orders', 'logs', 'cms', 'audit',
  'treasury', 'alerts', 'budgets', 'cardActions', 'upstreamCosts', 'cryptoPayments', 'profit',
];

const PWD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,64}$/; // 07 §3 / X-24：≥8 位且含字母与数字
const OTP_RE = /^\d{6}$/;
const BADGE_MS = 60_000; // C-24 徽标 60s 轮询 [本册定]

// 原型样式串逐字：L39 验证码框、L67 状态章与圆点
const OTP_STYLE = "height:46px;border:1px solid #dfe5e2;border-radius:10px;padding:0 14px;font-size:14px;outline:none;font-family:'JetBrains Mono',monospace;letter-spacing:2px";
const STEP_BASE = 'display:inline-flex;align-items:center;gap:6px;height:28px;padding:0 12px;border-radius:999px;font-size:12px;font-weight:700;white-space:nowrap';

function setSh(ctx: AdminCtx, patch: Partial<ShellSlice>): void {
  ctx.host.setState({ sh: { ...ctx.host.state.sh, ...patch } });
}

function patchOnb(ctx: AdminCtx, p: Partial<OnbState>): void {
  const cur = ctx.host.state.sh.onb;
  if (cur) setSh(ctx, { onb: { ...cur, ...p } });
}

// ---- 徽标（C-24：60s；无 view 不请求；logs / alerts 取 GET /me 的 badges，不单独请求） ----

// logs / alerts 两项：GET /me 的 badges（后端无 logs.view 时已置 0；前端再按 canSee 兜底）
export function badgesFromMe(me: AdminMe | null): Pick<Record<BadgeKey, number>, 'logs' | 'alerts'> {
  if (!me) return { logs: 0, alerts: 0 };
  return {
    logs: canSee(me, 'logs') ? me.badges.webhooks_failed : 0,
    alerts: canSee(me, 'alerts') ? me.badges.alerts_open : 0,
  };
}

interface HostRun {
  gen: number; // 会话代：session:lost / session:ready 各 +1，在途徽标结果写入前代已变即丢弃
  poller: Poller | null;
}

const runs = new WeakMap<DcHost<AdminState>, HostRun>();
const unsubs = new WeakMap<DcHost<AdminState>, ReadonlyArray<() => void>>();

function runOf(host: DcHost<AdminState>): HostRun {
  let r = runs.get(host);
  if (!r) {
    r = { gen: 0, poller: null };
    runs.set(host, r);
  }
  return r;
}

async function pendingTotal(ctx: AdminCtx, route: 'GET /api/admin/reviews' | 'GET /api/admin/agent-withdrawals', passive: boolean): Promise<number> {
  try {
    // 后端 size 只接受 10 / 20 / 50（06 C-24 写 size=1 不可用）
    const r = await ctx.gp.api.call<AdminPage<unknown>>(route, { query: { tab: 'pending', page: 1, size: 10 }, passive });
    return r.total;
  } catch (e) {
    console.warn('[gp:admin] 徽标计数失败', route, e); // 徽标失败不打扰用户，保留旧值
    return -1;
  }
}

// passive：60s 轮询 tick 发出的请求带 X-Activity: passive（不刷新服务端最后活跃）；profile 事件（写后刷新）为主动请求。
// withMe：60s 轮询 tick 并发重取 /me（logs / alerts 徽标与告警铃取其 badges；refreshMe 自带会话代丢弃且失败静默）。
// profile 事件只重算 review / agentWd：写 logs / alerts 的动作在发 profile 前已显式 refreshMe（06 C-24「60s 轮询 + 写后立即刷新」）
export async function refreshBadges(ctx: AdminCtx, withMe = false, passive = false): Promise<void> {
  const host = ctx.host;
  const me = host.state.sh.me;
  if (!me || host.state.screen !== 'app') return;
  const run = runOf(host);
  const my = run.gen;
  const [review, agentWd] = await Promise.all([
    canSee(me, 'review') ? pendingTotal(ctx, 'GET /api/admin/reviews', passive) : Promise.resolve(0),
    canSee(me, 'agentWd') ? pendingTotal(ctx, 'GET /api/admin/agent-withdrawals', passive) : Promise.resolve(0),
    withMe ? ctx.gp.session.refreshMe({ passive }) : Promise.resolve(),
  ]);
  if (run.gen !== my || runs.get(host) !== run) return;
  const sh = host.state.sh;
  const prev = sh.badges;
  host.setState({
    sh: { ...sh, badges: { ...prev, review: review < 0 ? prev.review : review, agentWd: agentWd < 0 ? prev.agentWd : agentWd, ...badgesFromMe(sh.me) } },
  });
}

function startBadges(ctx: AdminCtx): void {
  const run = runOf(ctx.host);
  run.gen++;
  run.poller?.stop();
  // 首个 tick 紧随 session:ready（/me 刚由 established 取回），不重复取 /me；此后每 60s 一并刷新 /me
  let first = true;
  run.poller = createPoller(() => {
    const withMe = !first;
    first = false;
    return refreshBadges(ctx, withMe, true);
  }, BADGE_MS);
  run.poller.start();
}

function stopBadges(ctx: AdminCtx): void {
  const run = runOf(ctx.host);
  run.gen++;
  run.poller?.stop();
  run.poller = null;
  const sh = ctx.host.state.sh;
  if (Object.values(sh.badges).some((n) => n !== 0)) ctx.host.setState({ sh: { ...sh, badges: { ...BADGES0 } } });
}

// ---- 登录与向导（C-01 / C-01b） ----

export async function doLogin(ctx: AdminCtx): Promise<void> {
  const s = ctx.host.state;
  const t = ctx.t;
  // 登录表单只在 login 屏渲染；booting（会话恢复中）/ app 屏不受理，防与 boot 的会话恢复并发 established
  if (s.screen !== 'login') return;
  const account = s.adminUser.trim().toLowerCase();
  const totp = s.sh.loginOtp.replace(/\s+/g, '');
  if (!account || !s.sh.loginPwd) return setSh(ctx, { loginErr: str(t, 'err1001'), otpErr: false });
  if (totp && !OTP_RE.test(totp)) return setSh(ctx, { loginErr: str(t, 'err1006'), otpErr: true });
  await ctx.gp.ui.run('login', async () => {
    try {
      const password = ctx.host.state.sh.loginPwd;
      const r = await ctx.gp.api.call<AdminLoginResp>('POST /api/public/admin/login', { body: totp ? { account, password, totp } : { account, password } });
      setSh(ctx, { loginPwd: '', loginOtp: '', loginErr: '', otpErr: false });
      // restricted = !totp_enabled || must_change_password（后端口径）；三者任一成立都按受限窗口处理
      if (r.restricted || r.must_change_password || !r.totp_enabled) {
        ctx.gp.session.restricted(r.token, r);
        if (!r.must_change_password) await enterStep2(ctx);
        return;
      }
      await ctx.gp.session.established(r.token, { persist: true, reason: 'login' });
      ctx.gp.ui.toast(str(t, 'toastLogin'));
    } catch (e) {
      // C-01：1004 → 验证码框红描边 + err1004；1006 → err1006 并清空验证码框；其余（1001 / 1002 / 网络）→ 红条
      const code = e instanceof ApiError ? e.code : 0;
      setSh(ctx, { loginErr: ctx.gp.ui.err(e), otpErr: code === 1004 || code === 1006, ...(code === 1006 ? { loginOtp: '' } : {}) });
    }
  });
}

async function enterStep2(ctx: AdminCtx): Promise<void> {
  try {
    const r = await ctx.gp.api.call<{ secret: string; otpauth_uri: string }>('POST /api/admin/2fa/setup');
    const qr = await qrDataUrl(r.otpauth_uri, 160); // TD-836：本地生成，不经第三方服务
    patchOnb(ctx, { step: 2, secret: r.secret, qr, code: '', err: '' });
  } catch (e) {
    patchOnb(ctx, { step: 2, err: ctx.gp.ui.err(e) });
  }
}

export async function onbSubmit(ctx: AdminCtx): Promise<void> {
  const o = ctx.host.state.sh.onb;
  const t = ctx.t;
  if (!o) return;
  if (o.step === 1) {
    if (!o.old) return patchOnb(ctx, { err: str(t, 'errOldPwd') });
    if (!PWD_RE.test(o.nw) || o.nw !== o.nw2 || o.nw === o.old) return patchOnb(ctx, { err: str(t, 'errPwdRule') });
    await ctx.gp.ui.run('onb', async () => {
      try {
        await ctx.gp.api.call('PUT /api/admin/my-password', { body: { old: o.old, new: o.nw } });
        // 02 §6.1：{relogin:true}，全部会话（含当前受限会话）失效；重登若 totp 未绑仍进步骤②（S-86）
        ctx.gp.session.relogin('toastMyPwd', 'pwd_changed');
      } catch (e) {
        const code = e instanceof ApiError ? e.code : 0;
        patchOnb(ctx, { err: code === 1001 ? str(t, 'errOldPwd') : code === 4220 ? str(t, 'errPwdRule') : ctx.gp.ui.err(e) });
      }
    });
    return;
  }
  const code = o.code.replace(/\s+/g, '');
  if (!OTP_RE.test(code)) return patchOnb(ctx, { err: str(t, 'err1006') });
  await ctx.gp.ui.run('onb', async () => {
    try {
      await ctx.gp.api.call('POST /api/admin/2fa/confirm', { body: { code } });
      ctx.gp.session.relogin('toastOnboardDone', 'onboard_done'); // SP2 OD-4：完成后强制重登
    } catch (e) {
      patchOnb(ctx, { err: ctx.gp.ui.err(e), code: '' });
    }
  });
}

// ---- 外壳 ----

function go(ctx: AdminCtx, page: Page): void {
  // 原型 L682 nav.go：切页复位页签；查询条件 / 排序 / 分页复位与加载条（E-10）由 table 域在 page / tab 变化时重载完成。
  // 同页同页签重复点击时 state 不变、不触发 update：发 screen{from===to} 让 table 域同样复位并重载（nav.go 每次点击都复位）
  // 运营 UX 评审 P0 #3：系统参数页有未保存修改时先弹页面内确认，确认后由 settings 域完成切页
  if (interceptSettingsLeave(ctx, page)) return;
  const same = ctx.s.page === page && ctx.s.tab === 0;
  ctx.host.setState({ page, tab: 0 });
  if (same) ctx.events.emit('screen', { from: page, to: page });
  void ctx.gp.session.refreshMe(); // G-10 第 5 条：页面切换时刷新
}

function navVals(ctx: AdminCtx): Array<Record<string, unknown>> {
  const { s, t } = ctx;
  const me = s.sh.me;
  const out: Array<Record<string, unknown>> = [];
  let group: Record<string, unknown> | null = null;
  for (const n of NAV) {
    if (n.kind === 'group') {
      // 分组标题延后到组内首个可见项再输出：组内无可见项则整组省略（G-10 第 1 条）
      group = { isGroup: true, isItem: false, label: str(t, n.label), groupStyle: `padding:12px 10px 4px;font-size:10px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:rgba(255,255,255,.35);${s.sidebar ? '' : 'display:none'}` };
      continue;
    }
    if (!canSee(me, n.page)) continue;
    if (group) {
      out.push(group);
      group = null;
    }
    const on = s.page === n.page;
    out.push({
      isGroup: false,
      isItem: true,
      label: str(t, n.page),
      icon: n.icon,
      badge: n.page==='cryptoPayments'?(me?.badges.crypto_exceptions??0):(n.badge ? s.sh.badges[n.badge] : 0),
      go: () => go(ctx, n.page),
      style: `display:flex;align-items:center;gap:10px;height:36px;padding:0 10px;border:0;border-radius:9px;cursor:pointer;font-size:13px;font-weight:600;background:${on ? 'rgba(57,200,143,.18)' : 'transparent'};color:${on ? '#39c88f' : 'rgba(255,255,255,.7)'};justify-content:${s.sidebar ? 'flex-start' : 'center'}`,
    });
  }
  return out;
}

function tabsVals(ctx: AdminCtx): { tabs: unknown; hasTabs: unknown } {
  const def = pageDefOf(ctx.s.page);
  // 非表格页（dashboard / settings / upstream / roles）原型 tabsFor 无对应项：无页签
  if (!def) return { tabs: [], hasTabs: false };
  const labels = def.tabLabels(ctx.t);
  // 表格重载由 table 域 update 检测 tab 变化完成，外壳只切下标
  return { tabs: labels.map((label, i) => ({ label, pick: () => ctx.host.setState({ tab: i }), style: tabStyle(ctx.s.tab === i) })), hasTabs: labels.length > 0 };
}

function stepStyle(state: 'cur' | 'done' | 'todo'): string {
  return `${STEP_BASE};${state === 'cur' ? 'background:#0f1a17;color:#fff' : state === 'done' ? 'background:#e3f6ee;color:#0d9a68' : 'background:#f3f5f4;color:#8a958f'}`;
}

function onbVals(ctx: AdminCtx): Record<string, unknown> {
  const t = ctx.t;
  const o = ctx.s.sh.onb ?? { step: 1, old: '', nw: '', nw2: '', secret: '', qr: '', code: '', err: '' };
  const step1 = o.step === 1;
  return {
    step1,
    step2: !step1,
    title: str(t, 'onbTitle'),
    sub: str(t, 'onbSub'),
    label1: `${step1 ? '①' : '✓'} ${str(t, 'onbStep1')}`,
    label2: `② ${str(t, 'onbStep2')}`,
    stepStyle1: stepStyle(step1 ? 'cur' : 'done'),
    stepStyle2: stepStyle(step1 ? 'todo' : 'cur'),
    ph: str(t, 'onbPwdPh'),
    tip: str(t, 'onbTotpTip'),
    copy: str(t, 'onbCopy'),
    secretLabel: str(t, 'onbSecret'),
    submit: str(t, step1 ? 'onbNext' : 'onbDone'),
    old: o.old,
    nw: o.nw,
    nw2: o.nw2,
    code: o.code,
    secretGrouped: o.secret.replace(/(.{4})/g, '$1 ').trim(),
    qr: o.qr,
    hasQr: !!o.qr,
    err: o.err,
  };
}

export const shell: AdminDomain = {
  name: 'shell',
  keys: KEYS,
  legacyKeysFor: () => [],
  i18n: { zh: { ...I18N_ADMIN_ERRORS.zh, ...I18N_ADMIN_SHELL.zh }, en: { ...I18N_ADMIN_ERRORS.en, ...I18N_ADMIN_SHELL.en } },
  rewrites: REWRITES_ADMIN_SHELL,
  // TD-822：外壳初值（screen 恒为 booting，不读 props.startLoggedIn；boot 结束切 app / login）
  state: () => ({ screen: 'booting', page: 'dashboard', tab: 0, adminUser: '', sh: shellDefaults() }),
  mount(ctx) {
    // 业务请求只在 session:ready 之后发（受限窗口只放行 my-password / 2fa / logout）；监听器幂等
    unsubs.get(ctx.host)?.forEach((off) => off());
    unsubs.set(ctx.host, [
      ctx.events.on('session:ready', () => {
        startBadges(ctx);
        startUpChip(ctx); // 06 C-24 上游状态章：endpoint-health?window=5m 的 summary，60s 轮询（TD-847）
      }),
      ctx.events.on('session:lost', () => stopBadges(ctx)),
      // 相关写操作成功后由各页 emit('profile') 触发立即刷新
      ctx.events.on('profile', () => void refreshBadges(ctx)),
    ]);
    void ctx.gp.session.boot();
  },
  update(ctx, prev) {
    const s = ctx.host.state;
    if (prev.page !== s.page) ctx.events.emit('screen', { from: prev.page, to: s.page });
    // /me 每次取回（established / refreshMe）即同步铃与 logs 徽标，不发额外请求
    if (prev.sh.me !== s.sh.me) ctx.host.setState({ sh: { ...s.sh, badges: { ...s.sh.badges, ...badgesFromMe(s.sh.me) } } });
  },
  unmount(ctx) {
    ctx.gp.session.dispose(); // 空闲计时与跨标签页通道
    stopUpChip(ctx.host);
    const run = runs.get(ctx.host);
    run?.poller?.stop();
    runs.delete(ctx.host);
    unsubs.get(ctx.host)?.forEach((off) => off());
    unsubs.delete(ctx.host);
  },
  vals(ctx) {
    const { s, t, lang } = ctx;
    const sh = s.sh;
    const me = sh.me;
    const page = s.page;
    const acct = me?.account ?? '';
    return {
      t,
      isBooting: s.screen === 'booting',
      isLogin: s.screen === 'login',
      isOnboard: s.screen === 'onboard',
      isApp: s.screen === 'app',
      adminUser: s.adminUser,
      onAdminUser: (e: InputEvent) => ctx.host.setState({ adminUser: e.target.value, sh: { ...ctx.host.state.sh, loginErr: '' } }),
      loginPwd: sh.loginPwd,
      onLoginPwd: (e: InputEvent) => setSh(ctx, { loginPwd: e.target.value, loginErr: '' }),
      loginOtp: sh.loginOtp,
      onLoginOtp: (e: InputEvent) => setSh(ctx, { loginOtp: e.target.value, loginErr: '', otpErr: false }),
      loginErr: sh.loginErr,
      // 空闲自动退出提示框（页面内，不用 window.confirm）：倒计时期间只有点「继续使用」才续期
      idle: {
        open: s.screen === 'app' && sh.idleLeft > 0,
        title: str(t, 'idleTitle'),
        text: interpolate(str(t, 'idleWarn'), { n: sh.idleLeft }),
        btn: str(t, 'idleContinue'),
        go: () => void ctx.gp.session.continueIdle(),
      },
      otpStyle: sh.otpErr ? `${OTP_STYLE};border-color:#d1434b` : OTP_STYLE,
      busyLogin: sh.busy === 'login',
      doLogin: () => void doLogin(ctx),
      logout: () => void ctx.gp.session.logout(),
      setZh: () => ctx.host.setState({ lang: 'zh' }),
      setEn: () => ctx.host.setState({ lang: 'en' }),
      toggleLang: () => ctx.host.setState({ lang: lang === 'zh' ? 'en' : 'zh' }),
      langLabel: lang === 'zh' ? 'EN' : '中文',
      zhBtn: `border:0;background:transparent;cursor:pointer;font-weight:700;color:${lang === 'zh' ? '#0d9a68' : '#9aa5a1'}`,
      enBtn: `border:0;background:transparent;cursor:pointer;font-weight:700;color:${lang === 'en' ? '#0d9a68' : '#9aa5a1'}`,
      sidebarOpen: s.sidebar,
      toggleSidebar: () => {
        const next = !ctx.host.state.sidebar;
        writeLocal('gpa_sidebar', next ? '1' : '0'); // C-24「折叠状态存 localStorage」
        ctx.host.setState({ sidebar: next });
      },
      sidebarStyle: `width:${s.sidebar ? '236px' : '64px'};flex:none;background:#0f1a17;display:flex;flex-direction:column;position:sticky;top:0;height:100vh;transition:width .2s`,
      nav: navVals(ctx),
      pageTitle: str(t, page),
      pageHint: str(t, 'h' + page.charAt(0).toUpperCase() + page.slice(1)),
      ...tabsVals(ctx),
      pg: {
        dashboard: page === 'dashboard',
        table: page === 'tgbot' ? s.tab === 0 : TABLE_PAGES.includes(page),
        upstream: page === 'upstream',
        roles: page === 'roles',
        settings: page === 'settings',
        tgbot: page === 'tgbot' && s.tab === 1,
        broadcast: page === 'broadcast', // 0049 C-33 原生整页（dash.broadcast）
      },
      acctName: acct,
      acctRole: me?.role ?? '',
      acctAvatar: acct.slice(0, 2).toUpperCase(),
      openMyPwd: () => openMyPwd(ctx),
      acctTg: { label: str(t, 'myTg'), open: () => openMyTg(ctx) }, // 0049 个人设置 Telegram 数字 ID（06 C-33；模板只直引原型 t.* 键，文案经 vals 下发）
      upChip: upChipVals(ctx),
      bell: { show: canSee(me, 'alerts'), count: sh.badges.alerts, go: () => go(ctx, 'alerts') },
      // G-10 第 1 条空态条：文案经 vals 下发（模板只直引原型 t.* 键），空串 = 不显示
      noPermAny: s.screen === 'app' && !!me && !hasAnyModule(me) && page === 'dashboard' ? str(t, 'noPermAny') : '',
      toast: s.toast,
      onb: onbVals(ctx),
      onOnb: {
        old: (e: InputEvent) => patchOnb(ctx, { old: e.target.value, err: '' }),
        nw: (e: InputEvent) => patchOnb(ctx, { nw: e.target.value, err: '' }),
        nw2: (e: InputEvent) => patchOnb(ctx, { nw2: e.target.value, err: '' }),
        code: (e: InputEvent) => patchOnb(ctx, { code: e.target.value, err: '' }),
      },
      onbSubmit: () => void onbSubmit(ctx),
      onbLogout: () => void ctx.gp.session.logout(),
      busyOnb: sh.busy === 'onb',
      copySecret: () => ctx.gp.ui.copy(ctx.host.state.sh.onb?.secret ?? ''),
    };
  },
};
